import { apiClient } from '../utils/api-client';
import { holdTypesService } from './hold-types';
import type { ClimbResponse, ClimbStatsResponse, Climb, ClimbStats, SendData, UserAttemptData, RecentBoulderSummary, RecentClimbPhoto, Hold, BoardHold, RouteLine } from '../types/climb';

// Interfaccia per i dati necessari alla creazione di un climb
interface CreateClimbData {
  type: 'boulder' | 'route' | 'board_climb';
  /**
   * Optional, like the model's own rule: a gym boulder goes up unnamed and is
   * named afterwards, if at all. A board problem and a competition problem are
   * still refused without one — see Climb#name_required?.
   */
  name?: string;
  grade_id: string | number;
  location_id?: string | number; // Absent for global board climbs
  board_id?: string | number; // Gym board climbs
  board_type_id?: string | number; // Global board climbs
  route_setter_id?: string | number; // Opzionale
  zone_id?: string | number; // Opzionale
  image_signed_id?: string;
  /**
   * La foto intera, quando quella salvata è un suo ritaglio. Non viene mostrata
   * da nessuna parte: serve al climb successivo tracciato sulla stessa parete
   * (vedi getRecentPhotos).
   */
  original_image_signed_id?: string;
  description?: string;
  hold_color?: string;
  climb_exceptions?: string[];
  position?: number;
  holds?: Hold[] | BoardHold[];
  line?: RouteLine | null; // Routes drawn as a line across the photo
  inclination?: number; // Board climbs on adjustable board types
  circuit?: boolean; // Board climbs whose holds are climbed in order
  forced_hands?: boolean; // Circuits whose holds also say which hand takes them
}

/**
 * What an edit may change on a climb already up.
 *
 * Every field is optional and only the ones present are written: a form showing
 * half of them must not blank the other half, and the drawing (`holds`, `line`)
 * travels on that same rule, which is what lets a metadata-only save leave the
 * marks alone. Not here on purpose: the location, the type and the confirmation
 * state — each is a move rather than a correction, with its own endpoint.
 */
export interface UpdateClimbData {
  name?: string;
  grade_id?: string | number;
  route_setter_id?: string | number | null;
  zone_id?: string | number | null;
  image_signed_id?: string;
  /** La foto intera, quando quella salvata è un suo ritaglio. */
  original_image_signed_id?: string;
  description?: string | null;
  hold_color?: string | null;
  climb_exceptions?: string[];
  position?: number | null;
  holds?: Hold[] | BoardHold[];
  line?: RouteLine | null;
  inclination?: number;
  circuit?: boolean;
  forced_hands?: boolean;
}

/** The scope a name has to be unique in — see climbsService.validateTitle. */
export interface ValidateTitleParams {
  title: string;
  /** Required unless the climb is a board problem, which has a board instead. */
  locationId?: string | number | null;
  type?: 'boulder' | 'route' | 'board_climb' | null;
  boardId?: string | number | null;
  /** Global boards (Kilter, Moonboard): the shared library the problem lives in. */
  boardTypeId?: string | number | null;
  /** Board problems only: variants at different angles may share a name. */
  inclination?: number | null;
  excludeClimbId?: string | null;
}

// Interfaccia per i parametri di ricerca
interface ClimbsSearchParams {
  type?: 'boulder' | 'route' | 'board_climb' | 'all';
  name?: string;
  location_id?: string | number;
  /**
   * The place by its slug instead of its id. What a page has before it has
   * asked for the place itself, which is what lets it ask for the climbs at
   * the same time (see prefetchClimbs).
   */
  location_slug?: string;
  board_id?: string | number;
  board_type_id?: string | number;
  grade_id?: string | number;
  route_setter_id?: string | number;
  created_by?: string; // User slug
  include_sends?: boolean;
  include_user_attempts?: boolean;
  climb_type?: string; // Single type filter
  zone_id?: string | number;
  scope?: string;
  page?: number;
  per_page?: number;
}

export interface ClimbsSearchResult {
  climbs: Climb[];
  meta?: {
    page: number;
    per_page: number;
    has_more: boolean;
  };
}

// Classe di errore personalizzata per errori di validazione JSON:API
export class ValidationError extends Error {
  public fieldErrors: Record<string, string[]>;
  public response: unknown;

  constructor(message: string, fieldErrors: Record<string, string[]>, response: unknown) {
    super(message);
    this.name = 'ValidationError';
    this.fieldErrors = fieldErrors;
    this.response = response;
  }
}

/**
 * How long a request sent ahead of time waits for the component that will
 * ask for it. Long enough for the page around it to load and mount; short
 * enough that a list left open is not answered from it later.
 */
const PREFETCH_TTL_MS = 30_000;

class ClimbsService {
  /**
   * The searches already on their way, by URL — see prefetchClimbs. Reused
   * rather than consumed: in development React mounts a component twice, and
   * the first mount's request would otherwise take the answer with it.
   */
  private readonly inFlightSearches = new Map<string, { promise: Promise<Climb[]>; startedAt: number }>();

  /**
   * Sends a search off before anything is waiting for it.
   *
   * The gym page knows its slug from the URL, but the list of climbs only
   * mounts once the gym itself has answered, so left to itself the slower of
   * the two requests would leave last. Fired here, it leaves with the first;
   * the identical `searchClimbs` call the list makes a moment later is
   * answered from it instead of asking again. A `reload` bypasses it, like it
   * bypasses every cache.
   */
  prefetchClimbs(params: ClimbsSearchParams = {}, fetchOptions?: Pick<RequestInit, 'cache'>): void {
    const url = this.buildSearchUrl(params);
    if (this.inFlightSearch(url)) return;

    const promise = this.requestClimbs(url, fetchOptions);
    // Nobody may ever come for it; a failure then is nobody's to handle.
    promise.catch(() => {});
    this.inFlightSearches.set(url, { promise, startedAt: Date.now() });
  }

  async searchClimbs(params: ClimbsSearchParams = {}, fetchOptions?: Pick<RequestInit, 'cache'>): Promise<Climb[]> {
    const url = this.buildSearchUrl(params);

    if (fetchOptions?.cache !== 'reload') {
      const prefetched = this.inFlightSearch(url);
      if (prefetched) return prefetched;
    }

    return this.requestClimbs(url, fetchOptions);
  }

  private inFlightSearch(url: string): Promise<Climb[]> | undefined {
    const entry = this.inFlightSearches.get(url);
    if (!entry) return undefined;
    if (Date.now() - entry.startedAt > PREFETCH_TTL_MS) {
      this.inFlightSearches.delete(url);
      return undefined;
    }
    return entry.promise;
  }

  private buildSearchUrl(params: ClimbsSearchParams): string {
    const searchParams = new URLSearchParams();

    if (params.type && params.type !== 'all') {
      searchParams.append('type', params.type);
    }
    if (params.name) {
      searchParams.append('name', params.name);
    }
    if (params.location_id) {
      searchParams.append('location_id', params.location_id.toString());
    }
    if (params.location_slug) {
      searchParams.append('location_slug', params.location_slug);
    }
    if (params.board_id) {
      searchParams.append('board_id', params.board_id.toString());
    }
    if (params.board_type_id) {
      searchParams.append('board_type_id', params.board_type_id.toString());
    }
    if (params.grade_id) {
      searchParams.append('grade_id', params.grade_id.toString());
    }
    if (params.route_setter_id) {
      searchParams.append('route_setter_id', params.route_setter_id.toString());
    }
    if (params.created_by) {
      searchParams.append('created_by', params.created_by);
    }
    if (params.include_sends) {
      searchParams.append('include_sends', 'true');
    }
    if (params.include_user_attempts) {
      searchParams.append('include_user_attempts', 'true');
    }
    if (params.climb_type) {
      searchParams.append('climb_type', params.climb_type);
    }
    if (params.zone_id) {
      searchParams.append('zone_id', params.zone_id.toString());
    }
    if (params.scope) {
      searchParams.append('scope', params.scope);
    }
    if (params.page) {
      searchParams.append('page', params.page.toString());
    }
    if (params.per_page) {
      searchParams.append('per_page', params.per_page.toString());
    }

    return `/api/climbs${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  }

  private async requestClimbs(url: string, fetchOptions?: Pick<RequestInit, 'cache'>): Promise<Climb[]> {
    const response = await apiClient.get(url, fetchOptions);

    if (!response.ok) {
      throw new Error('Failed to search climbs');
    }

    const data = await response.json();
    return data.data.map((item: { attributes: Climb; relationships?: { sends?: { data: SendData[] }; user_attempts?: { data: UserAttemptData[] } } }) => {
      const climb = item.attributes as Climb;
      // Include sends if present in relationships
      if (item.relationships?.sends?.data) {
        climb.sends = item.relationships.sends.data;
      }
      // Include user attempts if present in relationships
      if (item.relationships?.user_attempts?.data) {
        climb.user_attempts = item.relationships.user_attempts.data;
      }
      return climb;
    });
  }

  async searchClimbsWithMeta(params: ClimbsSearchParams = {}): Promise<ClimbsSearchResult> {
    const searchParams = new URLSearchParams();

    if (params.type && params.type !== 'all') {
      searchParams.append('type', params.type);
    }
    if (params.location_id) {
      searchParams.append('location_id', params.location_id.toString());
    }
    if (params.scope) {
      searchParams.append('scope', params.scope);
    }
    if (params.page) {
      searchParams.append('page', params.page.toString());
    }
    if (params.per_page) {
      searchParams.append('per_page', params.per_page.toString());
    }

    const url = `/api/climbs?${searchParams.toString()}`;
    const response = await apiClient.get(url, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to search climbs');
    }

    const data = await response.json();
    const climbs = data.data.map((item: { attributes: Climb }) => item.attributes as Climb);

    return {
      climbs,
      meta: data.meta,
    };
  }

  async getClimbBySlug(slug: string): Promise<Climb> {
    const response = await apiClient.get(`/api/climbs/${slug}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Climb not found');
      }
      throw new Error('Failed to fetch climb profile');
    }

    const data: ClimbResponse = await response.json();
    return data.data.attributes;
  }

  async getClimbStats(slug: string): Promise<ClimbStats> {
    const response = await apiClient.get(`/api/climbs/${slug}/stats`);

    if (!response.ok) {
      throw new Error('Failed to fetch climb stats');
    }

    const data: ClimbStatsResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Crea un nuovo climb (Boulder o Route)
   * @param climbData Dati del climb da creare
   * @returns Il climb creato
   */
  async createClimb(climbData: CreateClimbData): Promise<Climb> {
    // Prepara i dati nel formato JSON:API richiesto dal controller
    const requestData = {
      data: {
        type: climbData.type,
        attributes: {
          name: climbData.name,
          grade_id: climbData.grade_id,
          // Location is absent for global board climbs
          ...(climbData.location_id && { location_id: climbData.location_id }),
          // Board anchors (board climbs only)
          ...(climbData.board_id && { board_id: climbData.board_id }),
          ...(climbData.board_type_id && { board_type_id: climbData.board_type_id }),
          ...(climbData.inclination != null && { inclination: climbData.inclination }),
          // Route setter è opzionale
          ...(climbData.route_setter_id && { route_setter_id: climbData.route_setter_id }),
          // Zona/settore è opzionale
          ...(climbData.zone_id && { zone_id: climbData.zone_id }),
          // Descrizione è opzionale
          ...(climbData.description && { description: climbData.description }),
          // Inviamo l'immagine se presente
          ...(climbData.image_signed_id && { image_signed_id: climbData.image_signed_id }),
          // E la foto intera, se quella salvata è un ritaglio
          ...(climbData.original_image_signed_id && {
            original_image_signed_id: climbData.original_image_signed_id,
          }),
          // Colore prese è opzionale
          ...(climbData.hold_color && { hold_color: climbData.hold_color }),
          // Eccezioni di arrampicata sono opzionali
          ...(climbData.climb_exceptions && { climb_exceptions: climbData.climb_exceptions }),
          // Cerchi/prese disegnati nell'editor (opzionale)
          ...(climbData.holds && climbData.holds.length > 0 && { holds: climbData.holds }),
          // Linea della via disegnata nell'editor (opzionale, solo per le vie)
          ...(climbData.line && climbData.line.points.length > 1 && { line: climbData.line }),
          // Circuito: le prese vanno salite nell'ordine in cui sono inviate
          ...(climbData.circuit && { circuit: true }),
          // Mani obbligate: ogni presa dice anche con quale mano si prende
          ...(climbData.forced_hands && { forced_hands: true }),
          // Posizione per le falesie (opzionale)
          ...(climbData.position != null && { position: climbData.position })
        }
      }
    };

    const response = await apiClient.post('/api/climbs', requestData, { requireAuth: true });

    if (!response.ok) {
      await this.throwClimbWriteError(response, 'Errore durante la creazione del climb');
    }

    const data: ClimbResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Corregge un climb già pubblicato — i campi, il disegno, la foto.
   *
   * Solo gli attributi presenti vengono scritti (vedi UpdateClimbData): un form
   * che ne mostra metà non deve azzerare l'altra metà.
   * @param climbId ID o slug del climb
   * @param climbType Il tipo del climb, come lo vuole JSON:API
   */
  async updateClimb(
    climbId: string | number,
    climbType: 'boulder' | 'route' | 'board_climb',
    climbData: UpdateClimbData
  ): Promise<Climb> {
    // Un campo assente è "non toccare", un campo a null è "svuota": la
    // differenza è tutto il senso di questa chiamata, quindi si tengono le
    // chiavi che ci sono e si scartano solo gli undefined.
    const attributes = Object.fromEntries(
      Object.entries(climbData).filter(([, value]) => value !== undefined)
    );

    const response = await apiClient.patch(
      `/api/climbs/${climbId}`,
      { data: { type: climbType, attributes } },
      { requireAuth: true }
    );

    if (!response.ok) {
      await this.throwClimbWriteError(response, 'Errore durante il salvataggio del climb');
    }

    const data: ClimbResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Trasforma una create/update fallita nell'errore che i form sanno mostrare:
   * un ValidationError con i pointer JSON:API, così si può segnare il campo
   * sbagliato invece di dire genericamente che qualcosa non va.
   */
  private async throwClimbWriteError(response: Response, fallbackMessage: string): Promise<never> {
    const errorData = await response.json().catch(() => ({}));

    if (response.status === 400) {
      throw new Error(errorData.errors?.[0]?.detail || 'Tipo di climb non valido');
    }

    if (response.status === 403 || response.status === 404) {
      throw new Error(errorData.errors?.[0]?.detail || fallbackMessage);
    }

    if (response.status === 422) {
      // Parse JSON:API validation errors
      if (errorData.errors && Array.isArray(errorData.errors)) {
        const fieldErrors: Record<string, string[]> = {};

        interface JsonApiValidationError {
          source?: { pointer?: string };
          detail?: string;
        }

        errorData.errors.forEach((apiError: JsonApiValidationError) => {
          if (apiError.source && apiError.source.pointer) {
            // Extract field name from JSON:API pointer (e.g., "/data/attributes/name" -> "name")
            const fieldPath = apiError.source.pointer;
            const fieldName = fieldPath.split('/').pop();

            if (fieldName && apiError.detail) {
              if (!fieldErrors[fieldName]) {
                fieldErrors[fieldName] = [];
              }
              fieldErrors[fieldName].push(apiError.detail);
            }
          }
        });

        // Throw ValidationError with structured field errors
        const errorMessage = Object.values(fieldErrors).flat().join(', ') || 'Errori di validazione';
        throw new ValidationError(errorMessage, fieldErrors, { status: 422, data: errorData });
      }

      // Fallback for non-structured validation errors
      const validationErrors = errorData.errors?.map((err: Record<string, unknown>) => err.detail).join(', ') || 'Errori di validazione';
      throw new Error(validationErrors);
    }

    throw new Error(fallbackMessage);
  }

  /**
   * Conferma un climb (admin action)
   * @param climbId ID o slug del climb
   * @returns Il climb aggiornato
   */
  async confirmClimb(climbId: string | number): Promise<Climb> {
    const response = await apiClient.patch(`/api/climbs/${climbId}/confirm`, {}, { requireAuth: true });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 404) {
        throw new Error('Climb non trovato');
      }

      throw new Error(errorData.errors?.[0]?.detail || 'Errore durante la conferma del climb');
    }

    const data: ClimbResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Rifiuta un climb (admin action)
   * @param climbId ID o slug del climb
   * @returns Il climb aggiornato
   */
  async rejectClimb(climbId: string | number): Promise<Climb> {
    const response = await apiClient.patch(`/api/climbs/${climbId}/reject`, {}, { requireAuth: true });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 404) {
        throw new Error('Climb non trovato');
      }

      throw new Error(errorData.errors?.[0]?.detail || 'Errore durante il rifiuto del climb');
    }

    const data: ClimbResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Report a climb as no longer physically present. Notifies the location's
   * staff so they can review it and delete it.
   * @param climbId ID or slug of the climb
   */
  async reportRemoved(climbId: string | number): Promise<void> {
    const response = await apiClient.post(`/api/climbs/${climbId}/report_removed`, {}, { requireAuth: true });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 404) {
        throw new Error('Climb non trovato');
      }

      throw new Error(errorData.errors?.[0]?.detail || 'Errore durante la segnalazione del climb');
    }
  }

  /**
   * Elimina un climb (admin action)
   * @param climbId ID o slug del climb
   */
  async destroyClimb(climbId: string | number): Promise<void> {
    const response = await apiClient.delete(`/api/climbs/${climbId}`, { requireAuth: true });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 404) {
        throw new Error('Climb non trovato');
      }

      throw new Error(errorData.errors?.[0]?.detail || 'Errore durante l\'eliminazione del climb');
    }
  }

  /**
   * Le foto delle pareti su cui sono stati tracciati gli ultimi climb di questo
   * posto, per chi ne sta inserendo un altro.
   *
   * Una foto sola per parete, e sempre quella intera: un reset è una sessione,
   * non un climb, e il secondo problema disegnato su quello scatto vuole la
   * parete, non il ritaglio del primo. Riservate allo staff — vedi
   * Api::Locations::RecentClimbPhotosController.
   */
  async getRecentPhotos(
    locationId: string | number,
    type: 'boulder' | 'route'
  ): Promise<RecentClimbPhoto[]> {
    const response = await apiClient.get(
      `/api/locations/${locationId}/recent_climb_photos?type=${type}`,
      { requireAuth: true }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch recent climb photos');
    }

    const json = await response.json() as { data: RecentClimbPhoto[] };
    return json.data ?? [];
  }

  async getRecentBouldersForFollowedGyms(limit = 12): Promise<RecentBoulderSummary[]> {
    const response = await apiClient.get(`/api/feed?limit=${limit}`, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to fetch recent boulders');
    }

    type FeedClimb = {
      id: string | number;
      name: string;
      slug: string;
      type: 'Boulder' | 'Route';
      difficulty: string;
      image_small_url?: string | null;
      location: {
        id: string | number;
        name: string;
        slug: string;
        type: string;
      };
    };

    type FeedItem = {
      type: string;
      attributes?: {
        timestamp: string;
        climb?: FeedClimb;
      };
    };

    const data = await response.json() as { data?: FeedItem[] };
    const items = Array.isArray(data.data) ? data.data : [];

    return items
      .filter((item): item is FeedItem & { attributes: { timestamp: string; climb: FeedClimb } } =>
        item.type === 'new_climb' &&
        !!item.attributes?.climb &&
        item.attributes.climb.type === 'Boulder'
      )
      .map((item) => {
        const { climb, timestamp } = item.attributes;
        return {
          id: climb.id.toString(),
          name: climb.name,
          slug: climb.slug,
          type: 'Boulder' as const,
          difficulty: climb.difficulty,
          image_small_url: climb.image_small_url ?? null,
          location: {
            id: climb.location.id.toString(),
            name: climb.location.name,
            slug: climb.location.slug,
            type: climb.location.type,
          },
          created_at: timestamp,
        };
      });
  }

  async bulkDiscard(locationId: string | number, olderThanDays: number): Promise<{ discarded_count: number }> {
    const response = await apiClient.post('/api/climbs/bulk_discard', {
      location_id: locationId,
      older_than_days: olderThanDays
    }, { requireAuth: true });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to bulk discard climbs');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * The names on the place's hold-type list, for the plain autocompletes that
   * only ever wanted words — the scan editor's colour field, and the defaults
   * above it. Anything that shows the colours themselves reads the list through
   * `holdTypesService` instead.
   */
  async getHoldColors(locationId: string | number, query: string = ''): Promise<string[]> {
    const { types } = await holdTypesService.list(locationId, { query, perPage: 20 });
    return types.map((type) => type.name);
  }

  /**
   * Whether a name is already taken where the climb is about to go.
   *
   * Scoped exactly as the server's uniqueness rules are — a location's climbs
   * of that type, or a board anchor at that angle — so a `false` here means the
   * save will go through. See Api::ClimbsController#validate_title.
   */
  async validateTitle({
    title,
    locationId,
    type,
    boardId,
    boardTypeId,
    inclination,
    excludeClimbId,
  }: ValidateTitleParams): Promise<{
    exists: boolean;
    message?: string;
  }> {
    const searchParams = new URLSearchParams();
    searchParams.append('title', title.trim());
    if (locationId != null) searchParams.append('location_id', locationId.toString());
    if (type) searchParams.append('type', type);
    if (boardId != null) searchParams.append('board_id', boardId.toString());
    if (boardTypeId != null) searchParams.append('board_type_id', boardTypeId.toString());
    // Only sent when the board grades per angle: elsewhere the climb carries no
    // inclination of its own and the server matches on its absence.
    if (inclination != null) searchParams.append('inclination', inclination.toString());
    if (excludeClimbId) searchParams.append('exclude_climb_id', excludeClimbId);

    const response = await apiClient.get(
      `/api/climbs/validate_title?${searchParams.toString()}`,
      { showErrors: false } // Don't show toast on validation errors
    );

    if (!response.ok) {
      // Silent fail - don't block user if validation endpoint fails
      console.error('Title validation failed:', response.status);
      return { exists: false };
    }

    const data = await response.json();
    return {
      exists: data.exists || false,
      message: data.message
    };
  }

  async reorderClimbs(locationId: string, climbIds: string[]): Promise<void> {
    const response = await apiClient.post('/api/climbs/reorder', {
      location_id: locationId,
      climb_ids: climbIds,
    });

    if (!response.ok) {
      throw new Error('Failed to reorder climbs');
    }
  }

  /**
   * Records that this climb was pushed out to the OS share sheet.
   *
   * Best-effort by design and never awaited by the UI: the share is already on
   * its way, and a failed bookkeeping call must not surface as an error over a
   * share that worked. It exists because Google Analytics counts shares but not
   * *which* problem was shared — the number the gym panel's board dashboard
   * shows per problem.
   */
  recordShare(
    climbId: string | number,
    details: { template?: string | null; mediaType?: string | null } = {}
  ): void {
    apiClient
      .post(`/api/climbs/${climbId}/shares`, {
        data: {
          attributes: {
            template: details.template ?? undefined,
            media_type: details.mediaType ?? undefined,
          },
        },
      })
      .catch(() => {});
  }
}

export const climbsService = new ClimbsService();
export type { CreateClimbData, ClimbsSearchParams };
